import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { usePatientRepresentation } from './use-patient-representation';
import { useRepresentablePatients } from './use-representable-patients';

export type RepresentablePatient = ReturnType<typeof useRepresentablePatients>['representablePatients'][number];

/**
 * Composes the building blocks needed by any "who is respondent" screen to
 * react to an active PU-verified representation context:
 *  - the `PatientWeb.UseRepresentatives` feature flag,
 *  - the list of patients the logged-in user may represent,
 *  - the patient currently being represented (e.g. set via IdP), and
 *  - helpers to start/stop representation through the IdP redirect.
 *
 * By default the feature flag controls both identity resolution and the
 * picker. Callers which must honor an already-active authenticated
 * representation can resolve that identity independently of the picker.
 */
export function useRespondentRepresentation(options: { resolveCurrentRepresentation?: boolean } = {}) {
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const shouldResolveRepresentation = featureUseRepresentatives || options.resolveCurrentRepresentation === true;
	const {
		currentlyRepresentedPatient,
		representablePatients,
		error: representablePatientsError,
		isLoading: isLoadingRepresentablePatients,
		hasUnresolvedRepresentation,
	} = useRepresentablePatients({ enabled: shouldResolveRepresentation });
	const { startRepresentation, stopRepresentation } = usePatientRepresentation();

	return {
		featureUseRepresentatives,
		currentlyRepresentedPatient: shouldResolveRepresentation ? currentlyRepresentedPatient : null,
		representablePatients: featureUseRepresentatives ? representablePatients : [],
		representablePatientsError,
		isLoadingRepresentablePatients,
		hasUnresolvedRepresentation,
		startRepresentation,
		stopRepresentation,
	};
}
